import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DollarSign,
  Leaf,
  TreePine,
  Car,
  TrendingUp,
  Award,
  Share2,
  BarChart3,
  ShoppingBag,
  Loader2,
  Sparkles,
  Twitter,
  Facebook,
  Linkedin,
  Copy,
  Check,
  Package,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Droplets,
  Timer,
  HeartHandshake,
  Scale as ScaleIcon,
  UtensilsCrossed,
  FileDown,
  Bell,
  BellOff
} from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { impact, emailDigest } from '../../utils/api';

// Weekly Roll-up Card Component
function WeeklyRollupCard({ data }) {
  if (!data || data.length === 0) {
    return (
      <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
        <CardContent className="py-6 text-center">
          <Calendar className="w-12 h-12 mx-auto mb-2 text-purple-300" />
          <p className="text-gray-500">No weekly data yet</p>
        </CardContent>
      </Card>
    );
  }

  const currentWeek = data[data.length - 1];
  const previousWeek = data.length > 1 ? data[data.length - 2] : null;
  
  const savingsChange = previousWeek 
    ? ((currentWeek.savings - previousWeek.savings) / (previousWeek.savings || 1) * 100).toFixed(1)
    : 0;
  const ghgChange = previousWeek
    ? ((currentWeek.ghg_averted - previousWeek.ghg_averted) / (previousWeek.ghg_averted || 1) * 100).toFixed(1)
    : 0;

  return (
    <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Calendar className="w-5 h-5 text-purple-600" />
          This Week's Impact
        </CardTitle>
        <CardDescription>{currentWeek.week_label}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white/80 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              {previousWeek && (
                <div className={`flex items-center text-xs ${parseFloat(savingsChange) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {parseFloat(savingsChange) >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {Math.abs(savingsChange)}%
                </div>
              )}
            </div>
            <div className="text-2xl font-bold text-emerald-700">${currentWeek.savings.toFixed(2)}</div>
            <div className="text-xs text-gray-500">Consumer Savings</div>
          </div>
          
          <div className="bg-white/80 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <Leaf className="w-5 h-5 text-green-600" />
              {previousWeek && (
                <div className={`flex items-center text-xs ${parseFloat(ghgChange) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {parseFloat(ghgChange) >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {Math.abs(ghgChange)}%
                </div>
              )}
            </div>
            <div className="text-2xl font-bold text-green-700">{currentWeek.ghg_averted.toFixed(1)} kg</div>
            <div className="text-xs text-gray-500">CO₂ Averted</div>
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t flex justify-between text-sm">
          <span className="text-gray-500">{currentWeek.transaction_count} transactions</span>
          <span className="text-gray-500">{currentWeek.items_sold} items sold</span>
        </div>
      </CardContent>
    </Card>
  );
}

// Transaction Impact List
function TransactionImpactList({ transactions }) {
  if (!transactions || transactions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
        <p>No transactions yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {transactions.length > 0 && (transactions || []).map((tx, i) => (
        <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="font-medium text-sm">Order #{tx.order_id.slice(-8)}</div>
              <div className="text-xs text-gray-500">
                {new Date(tx.date).toLocaleDateString()} · {tx.item_count} items
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="flex gap-4">
              <div>
                <div className="font-bold text-emerald-600">${tx.savings.toFixed(2)}</div>
                <div className="text-xs text-gray-400">saved</div>
              </div>
              <div>
                <div className="font-bold text-green-600">{tx.ghg_averted.toFixed(1)} kg</div>
                <div className="text-xs text-gray-400">CO₂</div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Retailer Impact Certificate
function RetailerImpactCertificate({ certificate }) {
  const [copied, setCopied] = useState(false);
  
  if (!certificate) return null;
  
  const handleCopy = () => {
    navigator.clipboard.writeText(certificate.share_text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };
  
  const shareToTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(certificate.share_text)}`, '_blank');
  };
  
  const shareToLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://dealshaq.com')}`, '_blank');
  };

  return (
    <Card className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border-2 border-blue-200 overflow-hidden">
      <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-full -mr-16 -mt-16 opacity-50" />
      
      <CardHeader className="relative">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
            <Award className="w-6 h-6 text-white" />
          </div>
          <div>
            <CardTitle>Retailer Impact Certificate</CardTitle>
            <CardDescription>{certificate.retailer_name}</CardDescription>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="relative space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white/80 rounded-xl p-4 text-center">
            <DollarSign className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
            <div className="text-3xl font-bold text-emerald-600">${certificate.total_savings}</div>
            <div className="text-sm text-gray-600">Customer Savings Generated</div>
          </div>
          <div className="bg-white/80 rounded-xl p-4 text-center">
            <Leaf className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <div className="text-3xl font-bold text-green-600">{certificate.total_ghg_averted} kg</div>
            <div className="text-sm text-gray-600">CO₂ Emissions Averted</div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white/60 rounded-lg p-3 flex items-center gap-3">
            <TreePine className="w-6 h-6 text-green-600" />
            <div>
              <div className="font-bold text-green-700">{certificate.trees_equivalent}</div>
              <div className="text-xs text-gray-500">Trees Equivalent</div>
            </div>
          </div>
          <div className="bg-white/60 rounded-lg p-3 flex items-center gap-3">
            <Car className="w-6 h-6 text-blue-600" />
            <div>
              <div className="font-bold text-blue-700">{certificate.cars_off_road_days}</div>
              <div className="text-xs text-gray-500">Days Car Off Road</div>
            </div>
          </div>
        </div>
        
        <div className="pt-4 border-t">
          <p className="text-sm font-medium mb-3 flex items-center gap-2">
            <Share2 className="w-4 h-4" /> Share Your Business Impact
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={shareToTwitter} className="flex-1" data-testid="impact-share-twitter-btn">
              <Twitter className="w-4 h-4 mr-1" /> Twitter
            </Button>
            <Button size="sm" variant="outline" onClick={shareToLinkedIn} className="flex-1" data-testid="impact-share-linkedin-btn">
              <Linkedin className="w-4 h-4 mr-1" /> LinkedIn
            </Button>
            <Button size="sm" variant="outline" onClick={handleCopy} data-testid="impact-share-copy-btn">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2 text-center">Certificate ID: {certificate.certificate_id}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// Food-Diversion Hero — grocer's headline sustainability story
function FoodDiversionHero({ diversion }) {
  const d = diversion || {};
  const lbs = d.lbs_diverted || 0;
  const meals = d.meals_provided || 0;
  const co2 = d.co2e_avoided_kg || 0;

  return (
    <Card data-testid="food-diversion-hero" className="relative overflow-hidden border-0 bg-gradient-to-br from-emerald-600 via-green-600 to-teal-600 text-white">
      <div className="absolute top-0 right-0 w-56 h-56 bg-white/10 rounded-full -mr-28 -mt-28" />
      <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/10 rounded-full -ml-20 -mb-20" />
      <CardContent className="relative py-8">
        <div className="flex items-center gap-2 mb-2 text-emerald-100">
          <Leaf className="w-5 h-5" />
          <span className="text-sm font-medium uppercase tracking-wide">Food Diverted From Landfill</span>
        </div>
        <div className="text-4xl sm:text-5xl font-bold mb-3" data-testid="hero-lbs-diverted">
          {lbs.toLocaleString()} <span className="text-2xl sm:text-3xl font-semibold">lbs</span>
        </div>
        <div className="text-base sm:text-lg text-emerald-50 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>→ ~<strong data-testid="hero-meals">{meals.toLocaleString()}</strong> meals to tables</span>
          <span className="text-emerald-200/70">·</span>
          <span><strong data-testid="hero-co2">{co2.toLocaleString()}</strong> kg CO₂e avoided</span>
        </div>
        {d.estimated && (
          <p className="text-xs text-emerald-100/80 mt-4 max-w-xl" data-testid="hero-estimate-note">
            * Includes category-average weight estimates for items whose exact weight
            wasn't captured at scan time.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// Secondary diversion stat tiles
function DiversionStats({ diversion }) {
  const d = diversion || {};
  const tiles = [
    { label: 'Retail Value Recovered', value: `$${(d.retail_value_recovered || 0).toLocaleString()}`, icon: DollarSign, cls: 'text-emerald-600', testId: 'div-retail-value' },
    { label: 'Water Saved', value: `${(d.water_saved_gal || 0).toLocaleString()} gal`, icon: Droplets, cls: 'text-blue-600', testId: 'div-water' },
    { label: 'Near-Expiry Rescue Rate', value: d.near_expiry_rescue_rate != null ? `${d.near_expiry_rescue_rate}%` : '—', icon: Timer, cls: 'text-amber-600', testId: 'div-rescue-rate' },
    { label: 'Charitable Meals Funded', value: (d.charitable_meals_funded || 0).toLocaleString(), icon: HeartHandshake, cls: 'text-rose-600', testId: 'div-charity-meals' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="diversion-stats">
      {tiles.map((t) => (
        <Card key={t.label} data-testid={t.testId}>
          <CardContent className="pt-6">
            <t.icon className={`w-6 h-6 mb-2 ${t.cls}`} />
            <div className="text-2xl font-bold text-gray-900">{t.value}</div>
            <div className="text-sm text-gray-500">{t.label}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// Per-category pounds-diverted breakdown
function CategoryDiversionCard({ categories }) {
  const rows = categories || [];
  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500" data-testid="category-diversion-empty">
          <ScaleIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          No category data yet
        </CardContent>
      </Card>
    );
  }
  const max = Math.max(...rows.map((r) => r.lbs_diverted || 0), 1);
  return (
    <Card data-testid="category-diversion-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UtensilsCrossed className="w-5 h-5 text-emerald-600" /> Diverted by Category
        </CardTitle>
        <CardDescription>Pounds of food kept out of the landfill, by category</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {rows.map((r) => (
          <div key={r.category} data-testid={`category-row-${r.category}`}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="font-medium text-gray-700">{r.category}</span>
              <span className="text-gray-500">{(r.lbs_diverted || 0).toLocaleString()} lbs · ~{(r.meals || 0).toLocaleString()} meals</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"
                   style={{ width: `${Math.max(4, ((r.lbs_diverted || 0) / max) * 100)}%` }} />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// Main Retailer Impact Dashboard
export default function RetailerImpactDashboard() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [weeklyData, setWeeklyData] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [certificate, setCertificate] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [sendingDigest, setSendingDigest] = useState(false);
  const [greenDigestOn, setGreenDigestOn] = useState(true);
  const [reportMonth, setReportMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [downloadingReport, setDownloadingReport] = useState(false);

  const monthOptions = (() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      const label = dt.toLocaleString('en-US', { month: 'long', year: 'numeric' });
      opts.push({ value, label });
    }
    return opts;
  })();

  useEffect(() => {
    loadImpactData();
    loadDigestPref();
  }, []);

  const loadDigestPref = async () => {
    try {
      const res = await emailDigest.getPreferences();
      setGreenDigestOn(res.data?.preferences?.green_digest !== false);
    } catch (error) {
      console.error('Failed to load digest preference:', error);
    }
  };

  const handleToggleGreenDigest = async (checked) => {
    setGreenDigestOn(checked);
    try {
      await emailDigest.updatePreferences({ green_digest: checked });
      toast.success(checked ? 'Weekly Green Digest turned on' : 'Weekly Green Digest paused');
    } catch (error) {
      setGreenDigestOn(!checked);
      toast.error('Failed to update preference');
    }
  };

  const handleDownloadReport = async () => {
    setDownloadingReport(true);
    try {
      const res = await impact.monthlyReport(reportMonth);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `DealShaq_Impact_${reportMonth.replace('-', '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Monthly impact report downloaded');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleSendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await emailDigest.greenSend();
      toast.success(res.data?.message || 'Green digest emailed to you');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send green digest');
    } finally {
      setSendingDigest(false);
    }
  };

  const handlePreviewDigest = async () => {
    try {
      const res = await emailDigest.greenPreview();
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(res.data.html_preview);
        win.document.close();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load preview');
    }
  };

  const loadImpactData = async () => {
    setLoading(true);
    try {
      const [summaryRes, weeklyRes, txRes, certRes] = await Promise.all([
        impact.retailerSummary(),
        impact.retailerWeekly(12),
        impact.retailerTransactions(20),
        impact.retailerCertificate()
      ]);
      
      setSummary(summaryRes.data);
      setWeeklyData(weeklyRes.data.weekly_data || []);
      setTransactions(txRes.data.transactions || []);
      setCertificate(certRes.data);
    } catch (error) {
      console.error('Failed to load impact data:', error);
      setSummary({
        total_savings: 0,
        total_ghg_averted: 0,
        transaction_count: 0,
        items_sold: 0,
        trees_equivalent: 0,
        cars_off_road_days: 0
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-blue-500" />
            DealShaq Grocer Impact Dashboard
          </h2>
          <p className="text-gray-500">Track your environmental and customer impact</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handlePreviewDigest} data-testid="green-digest-preview-btn">
            <Calendar className="w-4 h-4 mr-2" /> Preview Digest
          </Button>
          <Button onClick={handleSendDigest} disabled={sendingDigest} className="bg-emerald-600 hover:bg-emerald-700" data-testid="green-digest-send-btn">
            {sendingDigest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Leaf className="w-4 h-4 mr-2" />}
            Email Me This Week's Recap
          </Button>
          <Button variant="outline" onClick={loadImpactData} data-testid="impact-refresh-btn">
            <TrendingUp className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Green Digest control + Monthly report */}
      <Card data-testid="impact-tools-bar">
        <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3" data-testid="green-digest-toggle-row">
            {greenDigestOn ? <Bell className="w-5 h-5 text-emerald-600" /> : <BellOff className="w-5 h-5 text-gray-400" />}
            <div>
              <div className="text-sm font-medium text-gray-900">Weekly Green Digest</div>
              <div className="text-xs text-gray-500">Automatic sustainability recap emailed every Monday</div>
            </div>
            <Switch
              checked={greenDigestOn}
              onCheckedChange={handleToggleGreenDigest}
              className="data-[state=checked]:bg-emerald-600 ml-1"
              data-testid="green-digest-toggle"
            />
          </div>
          <div className="flex items-center gap-2">
            <Select value={reportMonth} onValueChange={setReportMonth} data-testid="monthly-report-month-select">
              <SelectTrigger className="w-44" data-testid="monthly-report-month-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(monthOptions || []).map((m) => (
                  <SelectItem key={m.value} value={m.value} data-testid={`month-option-${m.value}`}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleDownloadReport} disabled={downloadingReport} variant="outline" data-testid="monthly-report-download-btn">
              {downloadingReport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              Monthly Report (PDF)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Food-Diversion Hero + sustainability KPIs */}
      <FoodDiversionHero diversion={summary?.diversion} />
      <DiversionStats diversion={summary?.diversion} />
      <CategoryDiversionCard categories={summary?.diversion?.category_diversion} />

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-2xl font-bold text-emerald-700">
                  ${summary?.total_savings?.toFixed(2) || '0.00'}
                </div>
                <div className="text-sm text-emerald-600">Customer Savings</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                <Leaf className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-2xl font-bold text-green-700">
                  {summary?.total_ghg_averted?.toFixed(1) || '0'} kg
                </div>
                <div className="text-sm text-green-600">CO₂ Averted</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center">
                <TreePine className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-2xl font-bold text-teal-700">
                  {summary?.trees_equivalent || 0}
                </div>
                <div className="text-sm text-teal-600">Trees Equivalent</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                <Package className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-700">
                  {summary?.items_sold || 0}
                </div>
                <div className="text-sm text-blue-600">Items Sold</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weekly Roll-up */}
      <WeeklyRollupCard data={weeklyData} />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="overview" data-testid="impact-tab-overview">Weekly Trends</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="impact-tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="certificate" data-testid="impact-tab-certificate">Certificate</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Weekly Performance</CardTitle>
              <CardDescription>Last 12 weeks of impact data</CardDescription>
            </CardHeader>
            <CardContent>
              {weeklyData.length > 0 ? (
                <div className="space-y-3">
                  {weeklyData.slice().reverse().slice(0, 8).map((week, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">{week.week_label}</div>
                        <div className="text-sm text-gray-500">{week.transaction_count} orders · {week.items_sold} items</div>
                      </div>
                      <div className="flex gap-6">
                        <div className="text-right">
                          <div className="font-bold text-emerald-600">${week.savings.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">savings</div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-green-600">{week.ghg_averted.toFixed(1)} kg</div>
                          <div className="text-xs text-gray-500">CO₂</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">No weekly data yet</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="transactions" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Recent Transactions</CardTitle>
              <CardDescription>Impact per transaction</CardDescription>
            </CardHeader>
            <CardContent>
              <TransactionImpactList transactions={transactions} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="certificate" className="mt-6">
          <RetailerImpactCertificate certificate={certificate} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
